import React, { useCallback, useState } from 'react';
import { Button, DatePicker } from '@guildeducationinc/recess';
import classnames from 'classnames';
import { useAuth } from '@guildeducationinc/guild-auth';
import { clickable } from '@src/analytics';
import { DirtyProgramCompletion } from '@src/components/Pages/EditProfile/ProgramCompletion/ProgramCompletion';
import { useLearningLifecyclesContext } from '@src/context/LearningLifecyclesContext';
import { parseDatesForMutation } from '@src/components/Pages/EditProfile/forms/ProgramCompletionDates';
import { useConfirmation } from '@src/context/ConfirmationDialogContext';
import useProgramCompletion from '@src/hooks/useProgramCompletion';
import { MilestoneKey } from '@src/context/MilestoneContext';
import { useIsMobile, useIsTablet } from '@src/utilities/screenResizeUtility';
import { useErrorBanner } from '@src/context/ErrorBannerContext';
import styles from './LifecycleDatePicker.module.scss';

export enum DatePickerAction {
  UpdateExpectedDate,
  SaveNewExpectedDate,
  SaveActualDate,
}

export interface LifecycleDatePickerProps {
  action?: DatePickerAction;
  setStartingSlide: (slide: number) => void;
}

const LifecycleDatePicker: React.FC<LifecycleDatePickerProps> = ({
  action = DatePickerAction.SaveNewExpectedDate,
  setStartingSlide,
}) => {
  const { currentLearningLifecycle, setCurrentLearningLifecycle } = useLearningLifecyclesContext();
  const [inputDate, setInputDate] = useState(
    currentLearningLifecycle?.expectedCompletionDate || undefined
  );
  const [rawInputDate, setRawInputDate] = useState<Date>();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const { user } = useAuth();
  const { openErrorBanner } = useErrorBanner();

  const { createProgramCompletion, updateProgramCompletion } = useProgramCompletion();
  const confirm = useConfirmation();

  const LABEL = action === DatePickerAction.UpdateExpectedDate ? 'Select new date' : 'Select date';

  const replaceMilestone = useCallback(
    (milestoneKey: MilestoneKey, newMilestoneKey: MilestoneKey) => {
      const existingMilestoneIndex = currentLearningLifecycle?.milestones?.indexOf(milestoneKey);
      if (
        existingMilestoneIndex !== undefined &&
        existingMilestoneIndex > -1 &&
        setCurrentLearningLifecycle
      ) {
        const modifiedMilestones = [...(currentLearningLifecycle?.milestones || [])];
        const modifiedLifecycle = {
          ...currentLearningLifecycle,
          milestones: modifiedMilestones,
        };
        if (
          milestoneKey === MilestoneKey.LearnerSubmitExpectedCompletionDate ||
          (milestoneKey === MilestoneKey.CompleterExpectedCompletionDatePassed &&
            action === DatePickerAction.UpdateExpectedDate)
        ) {
          modifiedLifecycle.expectedCompletionDate = inputDate;
        } else if (
          milestoneKey === MilestoneKey.CompleterExpectedCompletionDatePassed &&
          action === DatePickerAction.SaveActualDate
        ) {
          modifiedLifecycle.actualCompletionDate = inputDate;
        }
        modifiedLifecycle.milestones[existingMilestoneIndex] = newMilestoneKey;
        setCurrentLearningLifecycle(modifiedLifecycle);
        if (isMobile || isTablet) setStartingSlide(existingMilestoneIndex);
      }
    },
    [
      action,
      currentLearningLifecycle,
      setCurrentLearningLifecycle,
      inputDate,
      isMobile,
      isTablet,
      setStartingSlide,
    ]
  );

  const createCompletion = async (completion: DirtyProgramCompletion) => {
    const response = await createProgramCompletion({
      variables: {
        completionData: completion,
      },
    });
    if (response.data?.createCompletion) {
      replaceMilestone(
        MilestoneKey.LearnerSubmitExpectedCompletionDate,
        MilestoneKey.SubmittedExpectedCompletionDate
      );
    } else {
      openErrorBanner();
    }
  };

  const updateCompletion = async (completion: DirtyProgramCompletion) => {
    const { id, ...newData } = completion;
    const response = await updateProgramCompletion({
      variables: {
        completionId: id || '',
        completionData: newData,
      },
    });

    if (response.data?.updateCompletion?.completion) {
      if (action === DatePickerAction.SaveActualDate) {
        replaceMilestone(
          MilestoneKey.CompleterExpectedCompletionDatePassed,
          MilestoneKey.CompleterActualCompletionDateSaved
        );
      } else if (action === DatePickerAction.UpdateExpectedDate) {
        replaceMilestone(
          MilestoneKey.CompleterExpectedCompletionDatePassed,
          MilestoneKey.SubmittedExpectedCompletionDate
        );
      }
    } else {
      openErrorBanner();
    }
  };

  const saveCompletionDateClickable = () => {
    clickable({
      loggedIn: true,
      product: 'home',
      tag: 'llc_completion_date',
      text: 'save',
      type: 'button',
    });
  };

  const confirmSaveClickable = () => {
    clickable({
      loggedIn: true,
      product: 'home',
      tag: 'llc_completion_date_modal',
      text: 'save',
      type: 'button',
    });
  };

  const cancelSaveClickable = () => {
    clickable({
      loggedIn: true,
      product: 'home',
      tag: 'llc_completion_date_modal',
      text: 'cancel',
      type: 'button',
    });
  };

  let completionDateData = {
    id: currentLearningLifecycle?.completionUuid,
  } as DirtyProgramCompletion;

  const handleCreateCompletion = () => {
    completionDateData = {
      studentUuid: user?.id,
      academicPartnerName: currentLearningLifecycle?.program?.studentFacingAcademicPartner?.name,
      academicPartnerUuid: currentLearningLifecycle?.program?.studentFacingAcademicPartner?.uuid,
      programName: currentLearningLifecycle?.program?.name,
      programUuid: currentLearningLifecycle?.program?.uuid,
      programType: currentLearningLifecycle?.program?.programKind?.toLowerCase(),
      expectedGraduationDate: inputDate,
    };
    createCompletion(parseDatesForMutation(completionDateData));
  };

  const handleUpdateCompletion = () => {
    completionDateData = {
      ...completionDateData,
      expectedGraduationDate: inputDate,
    };
    updateCompletion(parseDatesForMutation(completionDateData));
  };

  const handleFinalUpdateCompletion = () => {
    completionDateData = {
      ...completionDateData,
      actualGraduationDate: inputDate,
    };
    confirm({
      catchOnCancel: false,
      title: `Save ${currentLearningLifecycle?.program?.name} completion date?`,
      descriptionElement: (
        <>
          You won&apos;t be able to change this information once you save it. Do you want to
          continue?
        </>
      ),
      customOnSubmit: () => confirmSaveClickable(),
      customOnCancel: () => cancelSaveClickable(),
    }).then(() => {
      updateCompletion(parseDatesForMutation(completionDateData));
    });
  };

  const handleSubmit = () => {
    const errorText = document.getElementById('lifecycle_date_picker-textinput-error');
    if (!errorText) {
      saveCompletionDateClickable();

      switch (action) {
        case DatePickerAction.SaveNewExpectedDate:
          handleCreateCompletion();
          break;

        case DatePickerAction.UpdateExpectedDate:
          handleUpdateCompletion();
          break;

        case DatePickerAction.SaveActualDate:
          handleFinalUpdateCompletion();
          break;
      }
    }
  };

  const containerClassNames = classnames(styles.container, 'flex', 'flexItems--end');

  return (
    <>
      <div className={containerClassNames}>
        <DatePicker
          errors={
            action === DatePickerAction.SaveActualDate && rawInputDate && rawInputDate > new Date()
              ? ['Please select a past date']
              : undefined
          }
          label={LABEL}
          id='lifecycleDatePicker'
          name='lifecycle_date_picker'
          onChange={(event) => {
            const date = new Date(event.target.value).toISOString();
            setInputDate(date);
            setRawInputDate(new Date(event.target.value));
          }}
          value={inputDate}
        />
        <Button
          type='submit'
          appearance='outline'
          theme='light'
          disabled={
            !inputDate ||
            (action === DatePickerAction.SaveActualDate &&
              rawInputDate &&
              rawInputDate > new Date())
          }
          onClick={() => {
            handleSubmit();
          }}
        >
          Save
        </Button>
      </div>
    </>
  );
};

export default LifecycleDatePicker;
